import {
  computeSetupChecklistItems,
  hasUpcomingHabitOccurrence,
  SetupChecklistItems,
} from '@griply/utils';
import { addDays, endOfDay, startOfDay } from 'date-fns';
import { useExternalCalendarConnections } from 'features/external-connections';
import { useGoals } from 'features/goals';
import {
  UPCOMING_WINDOW_DAYS,
  useActiveTasks,
  useAllCompletedTasks,
  useHabits,
  useTodaysCompletedHabits,
  useUpcomingTenDaysTasks,
} from 'features/tasks';
import { useMemo } from 'react';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

export const useSetupChecklistItems = (): SetupChecklistItems => {
  const user = useUser();
  const goals = useGoals();
  const habits = useHabits();
  const completedTasks = useAllCompletedTasks();
  const completedHabitsToday = useTodaysCompletedHabits();
  const activeTasks = useActiveTasks();
  const upcomingTasks = useUpcomingTenDaysTasks();
  const connections = useExternalCalendarConnections();
  const today = useToday();

  return useMemo(() => {
    const hasFutureHabit = hasUpcomingHabitOccurrence(habits, {
      startDate: startOfDay(addDays(today, 1)),
      endDate: endOfDay(addDays(today, UPCOMING_WINDOW_DAYS)),
      today,
      weekStartsOn: getWeekStartsOn(user),
    });

    return computeSetupChecklistItems({
      goalCount: goals.length,
      habitCount: habits.length,
      hasCompletedTaskOrHabit:
        !!completedTasks.length || !!completedHabitsToday.length,
      hasAnyTask: !!activeTasks.length || !!completedTasks.length,
      hasFutureTask: !!upcomingTasks.length || hasFutureHabit,
      hasCalendarConnection: !!connections.length,
    });
  }, [
    user,
    goals,
    habits,
    completedTasks,
    completedHabitsToday,
    activeTasks,
    upcomingTasks,
    connections,
    today,
  ]);
};
